import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Typography,
  Space,
  Button,
  Card,
  Modal,
  Spin,
  Empty,
  Tag,
  Row,
  Col,
  Input,
  Select,
  ConfigProvider,
} from "antd";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";
import {
  CheckCircleOutlined,
  FileDoneOutlined,
  StopOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import axios from "axios";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PATH } from "../../redux/utils";
import {
  actionAbogadoGetById,
  actionAbogadoUpdateById,
  actionAbogadoDeleteById,
} from "../../redux/actions/abogados/abogados";
import { HappyProvider } from "@ant-design/happy-work-theme";
import ExpedienteCards from "../../components/expedientes/ExpedienteCards";
import { showInsetEffect } from "../../utils/antdInsetWave";
import { DataBadge } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import "../../assets/css/LaboralExpedienteDetallePage.css";
import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";
import "../../components/tribunal/TribunalPage.css";

dayjs.locale("es");

const { Text, Title } = Typography;

const PAGE_SIZE = 12;

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const fmtDate = (d) => {
  if (!d) return "—";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD/MM/YYYY") : "—";
};

const fmtDateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const fmtDt = (d) => {
  if (!d) return "—";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD/MM/YYYY, HH:mm") : "—";
};

const normStr = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

const fmtExcel = (d) => {
  if (!d) return "";
  const x = dayjs(d);
  return x.isValid() ? x.format("M/D/YY H:mm") : "";
};

const api = axios.create({
  baseURL: PATH,
  headers: { "Content-Type": "application/json" },
});

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ===================== Actividad Tab =====================
function ActividadTab({ actividad, loading }) {
  const getActionMeta = (action) => {
    const a = String(action || "").toUpperCase();
    if (a.includes("CREATE") || a.includes("INSERT"))
      return { label: "Registro", tagColor: "success", dot: "#52c41a" };
    if (a.includes("UPDATE") || a.includes("EDIT") || a.includes("PATCH"))
      return { label: "Actualización", tagColor: "processing", dot: "#1677ff" };
    if (a.includes("DELETE"))
      return { label: "Eliminación", tagColor: "error", dot: "#ff4d4f" };
    return { label: toTitleCase(action || "Acción"), tagColor: "default", dot: "#8c8c8c" };
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card style={{ borderRadius: 12, marginTop: 4 }} bodyStyle={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <Text strong style={{ fontSize: 17, color: "#111827" }}>
          Actividad del abogado
        </Text>
        <div
          style={{
            background: "#f0f0f0",
            borderRadius: 20,
            padding: "2px 11px",
            fontSize: 13,
            fontWeight: 600,
            color: "#4b5563",
          }}
        >
          {actividad.length}
        </div>
      </div>

      {actividad.length === 0 ? (
        <Empty description="Sin actividad registrada para este abogado" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {actividad.map((item, idx) => {
            const { label, tagColor, dot } = getActionMeta(item.action);
            const isLast = idx === actividad.length - 1;
            return (
              <div key={item.id_audit_log ?? idx} style={{ display: "flex", gap: 14 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingTop: 17,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: dot,
                      boxShadow: `0 0 0 3px ${dot}22`,
                      flexShrink: 0,
                    }}
                  />
                  {!isLast && (
                    <div
                      style={{
                        width: 2,
                        flex: 1,
                        background: "#e5e7eb",
                        minHeight: 16,
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, paddingBottom: isLast ? 0 : 10 }}>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #f3f4f6",
                      borderRadius: 10,
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                      <Tag color={tagColor} style={{ margin: 0, fontWeight: 600, flexShrink: 0, fontSize: 12 }}>
                        {label}
                      </Tag>
                      <Text style={{ fontSize: 14, lineHeight: 1.45, color: "#1f2937" }}>
                        {item.message || "—"}
                      </Text>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 8 }}>
                      <Text style={{ fontSize: 12, color: "#9ca3af" }}>{fmtDt(item.datetime)}</Text>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                          {item.user_name || "—"}
                        </div>
                        {item.user_email && (
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>{item.user_email}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ===================== Conciliación Tab =====================
function ConciliacionTab({ idAbogado, abogadoNombre }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);

  const [search, setSearch] = useState("");
  const [idCiudad, setIdCiudad] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("todos");

  useEffect(() => {
    api
      .get("ciudades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCities(arr);
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(
    async (filters = {}) => {
      try {
        setLoading(true);
        const { data } = await api.post(
          "conciliaciones/cards",
          { id_abogado: Number(idAbogado), ...filters },
          { headers: authHeader() }
        );
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setItems(arr);
      } catch {
        notify.error({
          message: "Error",
          description: "No se pudieron cargar los expedientes de conciliación.",
        });
      } finally {
        setLoading(false);
      }
    },
    [idAbogado]
  );

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const t = setTimeout(() => { applyFilters(search, idCiudad, dateRange); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilters = (s, ciudad, range) => {
    const filters = {};
    if (s && s.trim()) filters.search = s.trim();
    if (ciudad) filters.id_ciudad = ciudad;
    if (range?.[0]) filters.date_from = range[0].format("YYYY-MM-DD");
    if (range?.[1]) filters.date_to = range[1].format("YYYY-MM-DD");
    fetchData(filters);
    setPage(1);
    setStatusFilter("todos");
  };

  const classifyConc = (e) => {
    const s = normStr(e.status || e.status_nombre || e.estatus || "");
    if (s.includes("convenio") || s.includes("constancia") || s.includes("cumpl")) return "convenios";
    if (s.includes("concluido") || s.includes("archivo") || s.includes("no conciliaci")) return "concluidos";
    return "activos";
  };

  const counts = useMemo(() => {
    let activos = 0, convenios = 0, concluidos = 0;
    for (const e of items) {
      const c = classifyConc(e);
      if (c === "activos") activos++;
      else if (c === "convenios") convenios++;
      else concluidos++;
    }
    return { activos, convenios, concluidos };
  }, [items]);

  const filteredItems = useMemo(() => {
    if (statusFilter === "todos") return items;
    return items.filter((e) => classifyConc(e) === statusFilter);
  }, [items, statusFilter]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const toggle = (key) => {
    setStatusFilter((prev) => (prev === key ? "todos" : key));
    setPage(1);
  };

  const cityOptions = [
    { value: null, label: "Todas las ciudades" },
    ...cities.map((c) => ({ value: c.id_ciudad ?? c.id, label: c.nombre })),
  ];

  const handleExport = () => {
    const rows = items.map((exp) => {
      const citada =
        exp.razones_sociales?.[0]?.razon_social ||
        exp.razones_sociales?.[0]?.nombre ||
        exp.nombre_empresa || "";
      return {
        "Número de ID": exp.expediente || "",
        "Solicitante": toTitleCase(
          Array.isArray(exp.trabajadores)
            ? exp.trabajadores.map((t) => t?.nombre_trabajador).filter(Boolean).join(", ")
            : ""
        ),
        "Citada": citada,
        "Fecha y hr de audiencia": fmtExcel(exp.fecha_audiencia_inicial),
        "Convenio": exp.status || "",
        "Segunda audiencia": fmtExcel(exp.fecha_proxima_audiencia),
        "Fecha de pago": fmtExcel(exp.fecha_proximo_pago),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [20, 30, 40, 22, 18, 22, 18].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conciliación");
    XLSX.writeFile(wb, `conciliacion_${String(abogadoNombre || "abogado").replace(/\s+/g, "_")}.xlsx`);
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16, marginBottom: 14, alignItems: "center" }}>
        <Input.Search
          placeholder="Buscar por trabajador, expediente, empresa…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => applyFilters(v, idCiudad, dateRange)}
          allowClear
          style={{ flex: "1 1 220px", maxWidth: 340 }}
        />
        <Select
          value={idCiudad}
          onChange={(val) => {
            setIdCiudad(val ?? null);
            applyFilters(search, val ?? null, dateRange);
          }}
          showSearch
          optionFilterProp="label"
          options={cityOptions}
          style={{ flex: "1 1 160px", maxWidth: 240 }}
        />
        <div style={{ display: "flex", gap: 8, flex: "1 1 240px", maxWidth: 300 }}>
          <ScrollDatePicker
            value={dateRange[0]}
            onChange={(d) => {
              const range = [d, dateRange[1] || null];
              setDateRange(range);
              applyFilters(search, idCiudad, range);
            }}
            format="DD/MM/YYYY"
            placeholder="Fecha desde"
            allowClear
          />
          <ScrollDatePicker
            value={dateRange[1]}
            onChange={(d) => {
              const range = [dateRange[0] || null, d];
              setDateRange(range);
              applyFilters(search, idCiudad, range);
            }}
            format="DD/MM/YYYY"
            placeholder="Fecha hasta"
            allowClear
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          <span style={{ fontWeight: 600, color: "#0f172a" }}>{filteredItems.length}</span>
          {" "}expediente{filteredItems.length !== 1 ? "s" : ""}
          {statusFilter !== "todos" && <span style={{ color: "#94a3b8" }}> de {items.length} total</span>}
        </div>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          className="laboral-btn-create"
          style={{ borderRadius: 8, fontWeight: 500, fontSize: 13 }}
        >
          Exportar Excel
        </Button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="laboral-stats-row" style={{ marginBottom: 20, gridTemplateColumns: "repeat(3, 1fr)" }}>
            <Card
              className={`laboral-stat-card laboral-stat-activos${statusFilter === "activos" ? " laboral-stat-active" : ""}`}
              hoverable
              onClick={() => toggle("activos")}
            >
              <Space align="center" size={10}>
                <div className="laboral-stat-icon"><CheckCircleOutlined /></div>
                <div>
                  <div className="laboral-stat-value">{counts.activos}</div>
                  <div className="laboral-stat-label">Activos</div>
                </div>
              </Space>
            </Card>
            <Card
              className={`laboral-stat-card laboral-stat-convenios${statusFilter === "convenios" ? " laboral-stat-active" : ""}`}
              hoverable
              onClick={() => toggle("convenios")}
            >
              <Space align="center" size={10}>
                <div className="laboral-stat-icon"><FileDoneOutlined /></div>
                <div>
                  <div className="laboral-stat-value">{counts.convenios}</div>
                  <div className="laboral-stat-label">Convenios</div>
                </div>
              </Space>
            </Card>
            <Card
              className={`laboral-stat-card laboral-stat-concluidos${statusFilter === "concluidos" ? " laboral-stat-active" : ""}`}
              hoverable
              onClick={() => toggle("concluidos")}
            >
              <Space align="center" size={10}>
                <div className="laboral-stat-icon"><StopOutlined /></div>
                <div>
                  <div className="laboral-stat-value">{counts.concluidos}</div>
                  <div className="laboral-stat-label">Concluidos</div>
                </div>
              </Space>
            </Card>
          </div>

          {filteredItems.length === 0 ? (
            <Card style={{ borderRadius: 12 }}>
              <Empty description="No se encontraron expedientes con los filtros aplicados" />
            </Card>
          ) : (
            <ExpedienteCards
              items={paginated}
              filteredExpedientes={filteredItems}
              PAGE_SIZE={PAGE_SIZE}
              currentPage={page}
              setCurrentPage={setPage}
              maps={{}}
              tipo="centro-conciliacion"
              persistSearch=""
            />
          )}
        </>
      )}
    </div>
  );
}

// ===================== Info Tab =====================
function Field({ label, value }) {
  return (
    <div className="laboral-exp-field">
      <span className="laboral-exp-field-label">{label}</span>
      <span className="laboral-exp-field-value">{value ?? "—"}</span>
    </div>
  );
}

function InfoTab({ detalle, idAbogado, dispatch, onUpdated, canEditar }) {
  const ciudades = Array.isArray(detalle.ciudades) ? detalle.ciudades : [];

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [form, setForm] = useState({ nombre: "", correo: "", ciudades_ids: [] });

  useEffect(() => {
    if (!editing) return;
    api
      .get("ciudades", { headers: authHeader() })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        setCiudadesOptions(arr.map((c) => ({ label: c.nombre, value: c.id_ciudad ?? c.id })));
      })
      .catch(() => {});
  }, [editing]);

  const startEdit = () => {
    setForm({
      nombre: detalle.nombre || "",
      correo: detalle.correo || "",
      ciudades_ids: ciudades.map((c) => c.id_ciudad ?? c.id ?? c),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!String(form.nombre || "").trim()) {
      notify.warning({ message: "El nombre es requerido", placement: "bottomRight" });
      return;
    }
    try {
      setSaving(true);
      await dispatch(
        actionAbogadoUpdateById(Number(idAbogado), {
          nombre: form.nombre.trim(),
          correo: form.correo.trim() || null,
          ciudades_ids: form.ciudades_ids,
        })
      );
      notify.success({ message: "Abogado actualizado", placement: "bottomRight" });
      setEditing(false);
      await onUpdated?.();
    } catch (e) {
      notify.error({
        message: "No se pudo guardar",
        description: e?.response?.data?.detail || e?.message,
        placement: "bottomRight",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className="laboral-exp-detail-card laboral-exp-detail-card-main p-2" style={{ marginTop: 18 }}>
        <div className="laboral-exp-card-header" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="laboral-exp-icon-badge">
              <i className="fa-solid fa-user-tie" />
            </div>
            <h3 className="laboral-exp-card-header-title">Datos del abogado</h3>
          </div>

          {canEditar && !editing && (
            <Button onClick={startEdit} className="laboral-exp-btn-clean">
              Editar
            </Button>
          )}
        </div>

        {editing ? (
          <div className="laboral-exp-edit-form">
            <div className="laboral-exp-edit-grid">
              <div className="laboral-exp-edit-field">
                <span className="laboral-exp-edit-label">Nombre completo</span>
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                />
              </div>

              <div className="laboral-exp-edit-field">
                <span className="laboral-exp-edit-label">Correo electrónico</span>
                <Input
                  type="email"
                  value={form.correo}
                  onChange={(e) => setForm((f) => ({ ...f, correo: e.target.value }))}
                />
              </div>

              <div className="laboral-exp-edit-field" style={{ gridColumn: "1 / -1" }}>
                <span className="laboral-exp-edit-label">Ciudades donde trabaja</span>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  style={{ width: "100%" }}
                  options={ciudadesOptions}
                  value={form.ciudades_ids}
                  onChange={(v) => setForm((f) => ({ ...f, ciudades_ids: v }))}
                />
              </div>
            </div>

            <div className="laboral-exp-edit-actions">
              <Button onClick={() => setEditing(false)} className="laboral-exp-btn-clean" disabled={saving}>
                Cancelar
              </Button>
              <HappyProvider>
                <Button
                  type="primary"
                  loading={saving}
                  onClick={handleSave}
                  className="laboral-exp-btn-save"
                  icon={<i className="fa-solid fa-floppy-disk" />}
                >
                  Guardar
                </Button>
              </HappyProvider>
            </div>
          </div>
        ) : (
          <div className="laboral-exp-field-grid">
            <Field label="Nombre completo" value={toTitleCase(detalle.nombre)} />
            <Field
              label="Correo electrónico"
              value={
                detalle.correo ? (
                  <a href={`mailto:${detalle.correo}`} style={{ color: "#0A539A" }}>
                    {detalle.correo}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Estado" value={Number(detalle.active) === 1 ? "Activo" : "Inactivo"} />
            <Field label="Fecha de registro" value={fmtDate(detalle.datetime)} />
          </div>
        )}
      </Card>

      <Card className="laboral-exp-detail-card p-2" style={{ marginTop: 18 }}>
        <div className="laboral-exp-card-header">
          <div className="laboral-exp-icon-badge">
            <i className="fa-solid fa-location-dot" />
          </div>
          <h3 className="laboral-exp-card-header-title">Ciudades donde trabaja</h3>
        </div>

        {ciudades.length === 0 ? (
          <Empty description="Sin ciudades asignadas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ciudades.map((c, i) => (
              <div
                key={i}
                style={{
                  background: "#DCE9F5",
                  border: "1px solid #b9d3ea",
                  borderRadius: 999,
                  padding: "5px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0A539A",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <i className="fa-solid fa-location-dot" style={{ fontSize: 11 }} />
                {c.nombre || c.ciudad || c}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ===================== Main Page =====================
export default function AbogadoDetallePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idAbogado } = useParams();
  const location = useLocation();

  const { perm } = usePermisos() || { perm: () => true };
  const canEditar = perm("abogados", "editar");
  const canEliminar = perm("abogados", "eliminar");

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const [actividad, setActividad] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);
  const [loadedAct, setLoadedAct] = useState(false);

  const reloadDetalle = useCallback(async () => {
    try {
      setLoadingDetalle(true);
      const data = await dispatch(actionAbogadoGetById(idAbogado));
      setDetalle(data || null);
    } catch {
      notify.error({
        message: "Error al cargar",
        description: "No se pudo obtener el detalle del abogado.",
      });
      setDetalle(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [dispatch, idAbogado]);

  // Load abogado
  useEffect(() => {
    reloadDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, idAbogado]);

  // Lazy load actividad
  const loadActividad = useCallback(async () => {
    if (loadedAct) return;
    try {
      setLoadingAct(true);
      const { data } = await api.get(`abogados/${idAbogado}/historial`, {
        headers: authHeader(),
        params: { limit: 200 },
      });
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setActividad(items);
    } catch {
      notify.error({ message: "Error", description: "No se pudo cargar la actividad." });
    } finally {
      setLoadingAct(false);
      setLoadedAct(true);
    }
  }, [idAbogado, loadedAct]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "actividad") loadActividad();
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar abogado",
      content: "Esta acción eliminará el abogado y su información relacionada. ¿Deseas continuar?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      okButtonProps: { loading: deleting, disabled: deleting },
      cancelButtonProps: { disabled: deleting },
      onOk: async () => {
        try {
          setDeleting(true);
          await dispatch(actionAbogadoDeleteById(Number(idAbogado)));
          notify.success({ message: "Abogado eliminado", description: "Se eliminó correctamente." });
          navigate("/abogados");
        } catch (error) {
          const msg = error?.response?.data?.detail || error?.message || "No se pudo eliminar el abogado.";
          notify.error({ message: "Error al eliminar", description: msg });
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loadingDetalle || !detalle) {
    return (
      <div className="lxp-root">
        <div className="lxp-wrap">
          <Card style={{ marginTop: 16, borderRadius: 12 }}>
            {loadingDetalle ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Text type="secondary">Sin datos del abogado.</Text>
            )}
          </Card>
        </div>
      </div>
    );
  }

  const abogadoNombre = detalle?.nombre || "abogado";

  const HEADER_TABS = [
    { key: "info", label: "Info", icon: <i className="fa-solid fa-user-tie" /> },
    { key: "conciliacion", label: "Centro Conciliación", icon: <i className="fa-solid fa-handshake" /> },
    { key: "actividad", label: "Actividad", icon: <i className="fa-solid fa-clock-rotate-left" /> },
  ];

  const ciudades = Array.isArray(detalle.ciudades) ? detalle.ciudades : [];
  const ciudadesLabel = ciudades.length
    ? ciudades.map((c) => c.nombre || c.ciudad || c).join(", ")
    : "Sin ciudades asignadas";
  const estatusPill = Number(detalle.active) === 1
    ? { label: "ACTIVO", kind: "primary" }
    : { label: "INACTIVO", kind: "gray" };

  return (
    <div className="lxp-root">
      <div className="lxp-wrap">
        {/* Summary card */}
        <div className="laboral-exp-main" style={{ background: "transparent", padding: 0, minHeight: "auto" }}>
        <ConfigProvider wave={{ showEffect: showInsetEffect }}>
        <Card className="laboral-exp-summary-card p-2">
          <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
            <Col flex="auto">
              <Space direction="vertical" size={8}>
                <div className="laboral-exp-summary-title">
                  <Space align="center" wrap>
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        fontSize: 28,
                        fontWeight: 650,
                        letterSpacing: "-0.01em",
                        lineHeight: 1.2,
                        color: "#0f172a",
                      }}
                    >
                      {toTitleCase(detalle.nombre).toUpperCase()}
                    </Title>
                    <DataBadge
                      color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                      className="d-inline-flex align-items-center gap-1 status-pill-badge"
                    >
                      {estatusPill.label}
                    </DataBadge>
                  </Space>
                  <Text type="secondary" className="laboral-exp-subtitle">
                    {detalle.correo || "Sin correo registrado"}
                  </Text>
                </div>

                <Space size="large" wrap className="laboral-exp-summary-meta">
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-location-dot" />
                    <Text>Ciudades: {ciudadesLabel}</Text>
                  </Space>
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-calendar-days" />
                    <Text>Registrado: {fmtDateLong(detalle.datetime)}</Text>
                  </Space>
                  <Space className="laboral-exp-meta-item">
                    <i className="fa-solid fa-address-card" />
                    <Text>Registrado por: {toTitleCase(detalle.nombre_creado_por)}</Text>
                  </Space>
                </Space>
              </Space>
            </Col>

            <Col flex="none">
              <Space>
                {canEliminar && (
                <Button
                  danger
                  type="primary"
                  icon={<i className="fa-solid fa-trash" />}
                  style={{ borderRadius: 8 }}
                  onClick={handleDelete}
                  loading={deleting}
                  disabled={deleting}
                >
                  Eliminar
                </Button>
                )}
              </Space>
            </Col>
          </Row>

          <div className="laboral-exp-header-tabs">
            {HEADER_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`laboral-exp-tab-btn ${activeTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                onClick={() => handleTabChange(t.key)}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
        </Card>
        </ConfigProvider>
        </div>

        {/* TAB CONTENT */}
        {activeTab === "info" && (
          <InfoTab detalle={detalle} idAbogado={idAbogado} dispatch={dispatch} onUpdated={reloadDetalle} canEditar={canEditar} />
        )}
        {activeTab === "conciliacion" && (
          <ConciliacionTab idAbogado={idAbogado} abogadoNombre={abogadoNombre} />
        )}
        {activeTab === "actividad" && (
          <ActividadTab actividad={actividad} loading={loadingAct} />
        )}
      </div>
    </div>
  );
}
